/**
 * DocumentCameraCapture (Jul-2026, User-Wunsch) — „direkter Scanner" per Kamera.
 *
 * Öffnet die Geräte-Kamera (Rückkamera bevorzugt, Webcam am Desktop), zeigt ein
 * Live-Bild und nimmt auf Klick ein Foto auf → liefert es als File via
 * `onCapture(file)`. Für das Abfotografieren von Lieferscheinen/Eingangsrechnungen.
 * Manueller Kamera-Wechsel als Sicherheitsnetz.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { X, Camera, SwitchCamera, Loader2 } from "lucide-react";

const isFront = (label = "") => /front|face|user|selfie|vorne/i.test(label);
const isRear = (label = "") => /back|rear|environment|rück|ruck|world|hinten/i.test(label);

export default function DocumentCameraCapture({ onCapture, onClose }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState(null);
    const [err, setErr] = useState("");
    const [starting, setStarting] = useState(true);

    const stop = useCallback(() => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        streamRef.current = null;
    }, []);

    // Geräte auflisten, Rückkamera vorwählen
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const warm = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
                warm.getTracks().forEach((t) => t.stop());
                const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
                if (!active) return;
                setDevices(list);
                const rear = list.find((d) => isRear(d.label));
                const nonFront = list.find((d) => !isFront(d.label));
                setDeviceId((rear || nonFront || list[0])?.deviceId || "");
            } catch (e) {
                if (active) { setErr("Kamera-Zugriff fehlgeschlagen: " + (e?.message || "unbekannt")); setStarting(false); }
            }
        })();
        return () => { active = false; };
    }, []);

    // Stream für gewähltes Gerät starten
    useEffect(() => {
        if (deviceId === null) return undefined;
        let active = true;
        setStarting(true);
        (async () => {
            try {
                stop();
                const constraints = deviceId
                    ? { video: { deviceId: { exact: deviceId } } }
                    : { video: { facingMode: { ideal: "environment" } } };
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
                streamRef.current = stream;
                if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
                setStarting(false);
            } catch (e) {
                if (active) { setErr("Kamera konnte nicht gestartet werden: " + (e?.message || "unbekannt")); setStarting(false); }
            }
        })();
        return () => { active = false; };
    }, [deviceId, stop]);

    useEffect(() => () => stop(), [stop]);

    const switchCamera = () => {
        if (devices.length < 2) return;
        setDeviceId((cur) => {
            const idx = devices.findIndex((d) => d.deviceId === cur);
            return devices[(idx + 1) % devices.length]?.deviceId || cur;
        });
    };

    const capture = () => {
        const v = videoRef.current;
        if (!v || !v.videoWidth) return;
        const canvas = document.createElement("canvas");
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        canvas.getContext("2d").drawImage(v, 0, 0);
        canvas.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], `lieferschein-${Date.now()}.jpg`, { type: "image/jpeg" });
            stop();
            onCapture(file);
        }, "image/jpeg", 0.92);
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black/85 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="doc-camera-capture">
            <div className="bg-white rounded-sm w-full max-w-lg overflow-hidden shadow-xl my-auto max-h-[96vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 bg-[#111827] text-white shrink-0">
                    <span className="text-sm font-bold uppercase tracking-wider flex items-center gap-2"><Camera className="w-4 h-4 text-[#FFCC00]" /> Lieferschein aufnehmen</span>
                    <div className="flex items-center gap-1">
                        {devices.length > 1 && (
                            <button type="button" onClick={switchCamera} data-testid="doc-camera-switch" aria-label="Andere Kamera" title="Andere Kamera" className="p-1.5 rounded-sm hover:bg-white/10"><SwitchCamera className="w-4 h-4" /></button>
                        )}
                        <button type="button" onClick={() => { stop(); onClose(); }} data-testid="doc-camera-close" aria-label="Schließen" className="p-1 rounded-sm hover:bg-white/10"><X className="w-5 h-5" /></button>
                    </div>
                </div>
                <div className="bg-black relative" style={{ aspectRatio: "4 / 3" }}>
                    <video ref={videoRef} className="w-full h-full object-contain" playsInline muted />
                    {starting && !err && (
                        <div className="absolute inset-0 flex items-center justify-center text-white/80"><Loader2 className="w-6 h-6 animate-spin" /></div>
                    )}
                </div>
                {err ? (
                    <p className="text-xs text-red-600 p-3" data-testid="doc-camera-error">{err}</p>
                ) : (
                    <div className="p-3 flex items-center justify-between gap-2">
                        <p className="text-[11px] text-gray-600">Beleg formatfüllend & scharf ausrichten, dann aufnehmen. Die KI erkennt danach die Positionen.</p>
                        <button type="button" onClick={capture} disabled={starting} className="shrink-0 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm px-4 py-2 text-sm uppercase tracking-wider disabled:opacity-50" data-testid="doc-camera-shoot">
                            <Camera className="w-4 h-4 mr-1 inline" /> Aufnehmen
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
